import { LoaderFunction } from 'react-router-dom';

// Sentry
import * as Sentry from '@sentry/react';

// GQL
import { client } from '../../../../gql/client';
import { GET_SELECTORS_DATA } from '../../../../gql/queries/orders/orders';
import { AuthContextType } from '../../../../hooks/useAuth';
import {
  FETCH_CAPITOLS,
  FETCH_CLASSIFICATIONS,
  FETCH_PREDEFINED_ORDERS,
} from '../../../../gql/queries/orders/predefinedData';
import { decompressSessionStorage } from '../../../../utils/sessionStorage';
import { resolveFreshRegisterDate } from '../../../../utils/freshRegisterDate';

// Data static
import { groupLanes, groupTracks } from '../../../../data/orders';

const loadFormOptions = async (
  userData: AuthContextType | null
) => {
  try {
    if (userData?.user) {
      const [
        { data },
        {
          data: { capitols },
        },
        {
          data: { classifications },
        },
        {
          data: { predefinedOrders },
        },
      ] = await Promise.all([
        client.query({
          query: GET_SELECTORS_DATA,
        }),
        client.query({
          query: FETCH_CAPITOLS,
        }),
        client.query({
          query: FETCH_CLASSIFICATIONS,
        }),
        client.query({
          query: FETCH_PREDEFINED_ORDERS,
        }),
      ]);

      return {
        ...data,
        capitols,
        classifications,
        groupTracks,
        groupLanes,
        predefinedOrders,
      };
    }
  } catch (err) {
    Sentry.captureException(err);
    return true;
  }
};

export const NewWorkOrderLoader: (
  userData: AuthContextType | null,
  sessionStorageKey: string
) => LoaderFunction | undefined =
  (userData, sessionStorageKey) => async () => {
  // El trabajo entero vive aquí dentro, íntegro. Solo ha dejado de
  // esperarse: la promesa tiene que llegar PENDIENTE al `<Await>` para que
  // el router termine la navegación al instante y el `<Fallback>` enseñe el
  // Loader DENTRO de la pantalla nueva.
  const load = async () => {
    const stringifyForm = sessionStorage.getItem(sessionStorageKey);
    const loaderToResolve = await loadFormOptions(userData);
    const nameOperator = `${userData?.user?.user?.name} ${
      userData?.user?.user.surName.split(' ')[0]
    }`;

    if (stringifyForm) {
      const sessionStorageData = JSON.parse(
        decompressSessionStorage(stringifyForm)
      );
      const combinedData = {
        // team field removed - users no longer have teamIDs
        ...loaderToResolve,
        ...sessionStorageData,
        // Después del borrador, para ganarle: su fecha pudo quedar congelada
        // en una visita anterior (ver `resolveFreshRegisterDate`).
        ...resolveFreshRegisterDate(sessionStorageData),
      };

      return combinedData;
    }

    // NO pre-cargamos concesión aquí (no es página inicial)
    // El componente manejará la selección a través de useEffect o props
    const combinedData = {
      // team field removed - users no longer have teamIDs
      ...loaderToResolve,
      operator: nameOperator,
      // También sin borrador: así el marcador existe desde la primera visita.
      ...resolveFreshRegisterDate(undefined),
    };

    return combinedData;
  };

  return { data: load() };
};
